import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useParams, useOutletContext } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';

// import ArchiveIcon from '@mui/icons-material/Archive';
import SaveIcon from '@mui/icons-material/Save';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import ClientActivationsButtonGroup from './ClientActivationsButtonGroup';

import { ClientEditFormSubmitProps, LinkedClientAccountData } from './types';

import {
  checkClientUserIdAvailability,
  getLinkedClientAccounts,
  getClientBirthDate,
  getClientSSN,
  reInitiateCustomerOnboarding,
  resetClientAccount,
  sendEmailConfirmation,
  updateClientRecord,
} from '../../api/clientsApi/clientsApi';
import Loader from '../../components/Loader';
import CommonButton from '../../components/buttons/CommonButton';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormLinkButton from '../../components/buttons/FormLinkButton';
import FormCopyTextField from '../../components/formFields/FormCopyTextField';
import FormDateField from '../../components/formFields/FormDateField';
import FormTextField from '../../components/formFields/FormTextField';
import FormMaskedTextField from '../../components/formFields/FormMaskedTextField';
import FormSelectField from '../../components/formFields/FormSelectField';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import { CLIENT_ACTIVATION_BUTTON_GROUPS, US_STATES_LIST } from '../../constants/constants';
import { CLIENT_EDIT_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import {
  clientEditFormSchema,
  personalInfoFormSchema,
} from '../../helpers/formSchemas/formSchemas';
import useSnackBar from '../../hooks/useSnackBar';
import { useHasPermission } from '../../hooks/useHasPermission';
import { ClientData, EntityFlexibleRecord } from '../../types';
import FormSSNField from '../../components/formFields/FormSSNField';
import ClientUpdateSSNDobForm from './ClientUpdateSSNDobForm';
import ClientActivationsContainer from './ClientActivationsContainer';
import OnboardingConfigurationCard from './OnboardingConfigurationCard';
import {
  normalizeOnboardingOptionValue,
} from './onboardingConfiguration';

const useStyles = makeStyles()((theme: Theme) => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  formContainer: {
    display: 'flex',
    flexBasis: '1184px',

    '@media (max-width: 800px)': {
      flexBasis: '480px',
    },
  },
  formWrapper: {
    display: 'flex',
    gap: '64px',

    '@media (max-width: 1000px)': {
      flexDirection: 'column',
      gap: '32px',
    },
  },
  formInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '32px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',
    flexBasis: '896px',

    '@media (max-width: 1000px)': {
      flexBasis: '416px',
    },

    '@media (max-width: 800px)': {
      padding: '12px',
    },
  },
  formInfoContainer: {
    display: 'flex',
    width: '100%',
    gap: '24px',

    '@media (max-width: 800px)': {
      flexDirection: 'column',
    },
  },
  formPlaceInfoContainer: {
    marginTop: '24px',
    display: 'flex',
    gap: '16px',
    flexDirection: 'column',

    '@media (max-width: 1000px)': {
      gap: '24px',
    },
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  formActionsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexBasis: '200px',

    '@media (max-width: 800px)': {
      flexBasis: 0,
    },
  },
  editPersonalInfoButton: {
    padding: 0,
    svg: {
      color: theme.palette.common.white,
    },
    '&.Mui-disabled': {
      svg: {
        color: 'rgba(255, 255, 255, 0.08)',
      },
    },
  },
  onboardingTimelineContainer: {
    width: '100%',
    maxWidth: '896px',
    padding: '24px 32px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 800px)': {
      padding: '16px 12px',
    },
  },
  onboardingTimelineList: {
    marginTop: '16px',
  },
  onboardingTimelineRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  onboardingTimelineIconColumn: {
    width: '24px',
    minWidth: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  onboardingTimelineConnector: {
    width: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    flexGrow: 1,
    minHeight: '28px',
    marginTop: '6px',
  },
  onboardingTimelineContent: {
    flex: 1,
    paddingBottom: '12px',
  },
  onboardingTimelineTitle: {
    fontWeight: 600,
    fontSize: '15px',
    lineHeight: '20px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  onboardingTimelineTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  onboardingTimelineHintIcon: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '16px',
  },
  onboardingTimelineMeta: {
    marginTop: '4px',
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '18px',
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'Inter',
  },
  onboardingTimelineMessage: {
    marginTop: '4px',
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '18px',
    color: 'rgba(255, 255, 255, 0.85)',
    fontFamily: 'Inter',
  },
  onboardingTimelineEmptyText: {
    marginTop: '16px',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: 'rgba(255, 255, 255, 0.75)',
    fontFamily: 'Inter',
  },
  onboardingTimelineInfoText: {
    marginTop: '16px',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '18px',
    color: 'rgba(255, 255, 255, 0.8)',
    fontFamily: 'Inter',
  },
  payoutAccordionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '16px',
  },
  payoutAccordionRoot: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    boxShadow: 'none',
    overflow: 'hidden',

    '&:before': {
      display: 'none',
    },
  },
  payoutAccordionSummary: {
    flexDirection: 'row-reverse',
    minHeight: '48px',
    padding: '0 16px',

    '& .MuiAccordionSummary-content': {
      margin: '12px 0',
    },
  },
  payoutAccordionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 600,
    fontSize: '15px',
    lineHeight: '20px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  payoutAccordionTitleItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  payoutAccordionTitleSeparator: {
    color: 'rgba(255, 255, 255, 0.4)',
  },
  payoutAccordionDetails: {
    padding: '4px 16px 20px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  currentStepBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '18px',
    whiteSpace: 'nowrap',

    '& svg': {
      fontSize: '18px !important',
    },
  },
  resendInviteChip: {
    height: '25px',
    borderRadius: '12px',
    border: '1px solid #38BDF8',
    background: '#38BDF814',
    '& .MuiChip-label': {
      fontFamily: 'Inter',
      fontSize: '13px',
      fontWeight: 600,
      color: '#38BDF8',
      padding: '4px 12px',
    },
    '&.Mui-disabled': {
      border: '1px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.08)',
    },
    '&.Mui-disabled .MuiChip-label': {
      color: 'rgba(255, 255, 255, 0.35)',
    },
  },
  linkedClaimantsContainer: {
    width: '100%',
    maxWidth: '896px',
    padding: '24px 32px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 800px)': {
      padding: '16px 12px',
    },
  },
  linkedClaimantsTitle: {
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.72)',
  },
  linkedClaimantsList: {
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  linkedClaimantRow: {
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
    '&:hover': {
      borderColor: 'rgba(255,255,255,0.3)',
    },
  },
  linkedClaimantName: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '18px',
    color: '#fff',
  },
  linkedClaimantMeta: {
    marginTop: '4px',
    fontFamily: 'Inter',
    fontSize: '12px',
    lineHeight: '16px',
    color: 'rgba(255,255,255,0.68)',
  },
  linkedClaimantActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: 'rgba(255,255,255,0.6)',
  },
  linkedClaimantChevron: {
    fontSize: '18px',
    color: 'rgba(255,255,255,0.5)',
  },
  linkedClaimantsHint: {
    marginTop: '12px',
    fontFamily: 'Inter',
    fontSize: '12px',
    lineHeight: '18px',
    color: 'rgba(255,255,255,0.62)',
  },
}));

type OnboardingProgressStep = {
  label: string;
  status: 'Done' | 'Right Tick' | 'In Progress' | 'Pending' | string;
  dateTime: string | null;
  message: string | null;
  failureType: string | null;
};

type OnboardingProgressGroup = {
  steps: OnboardingProgressStep[];
  paymentMethod: string | null;
  caseId?: number;
  caseReferenceId?: string;
  settlementId?: number;
};

type PayoutProgressGroup = NonNullable<ClientData['payoutProgress']>[number];

type SettlementProgressGroup = {
  caseId?: number;
  caseReferenceId?: string;
  settlementId?: number;
  onboarding?: OnboardingProgressGroup;
  payout?: PayoutProgressGroup;
  resendOnboardingInvite?: boolean;
};

const ClientsEditFormContainer = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { setAlert } = useSnackBar();
  const canEdit = useHasPermission('Clients', 'Edit');

  const { entityData } = useOutletContext<EntityFlexibleRecord>();
  const clientData = entityData as ClientData | undefined;
  const hasCognitoId =
    typeof clientData?.cognitoId === 'string' && clientData.cognitoId.trim().length > 0;
  const canEditEmailAndPhone = canEdit && !hasCognitoId;
  // userId belongs to the customer and is immutable once set; only legacy customers without one can get it here.
  const hasUserId = typeof clientData?.userId === 'string' && clientData.userId.trim().length > 0;
  const canEditUserId = canEdit && !hasCognitoId && !hasUserId;
  const onboardingConfiguration = clientData?.onboardingConfiguration;
  const firmConfigValue =
    clientData?.firmConfigValue ||
    (onboardingConfiguration && typeof onboardingConfiguration !== 'string'
      ? onboardingConfiguration.firmConfig || ''
      : '');

  const [isSSNValueVisible, setIsSSNValueVisible] = useState<boolean>(false);
  const [isSSNLoading, setIsSSNLoading] = useState<boolean>(false);
  const [isBirthDateValueVisible, setIsBirthDateValueVisible] = useState<boolean>(false);
  const [isBirthDateLoading, setIsBirthDateLoading] = useState<boolean>(false);
  const [localHasSsnDob, setLocalHasSsnDob] = useState<boolean>(false);
  const [isEditPersonalInfoPopupOpen, setIsEditPersonalInfoPopupOpen] = useState<boolean>(false);
  const [editPersonalFormData, setEditPersonalFormData] = useState<{
    ssn: string;
    birthdate: Date | null;
  }>({
    ssn: '',
    birthdate: null,
  });
  const [accountActivationsState, setAccountActivationsState] = useState({
    email: {
      label: 'EMAIL',
      isActive: false,
    },
    ssn: {
      label: 'SSN',
      isActive: false,
    },
    dob: {
      label: 'DATE OF BIRTH',
      isActive: false,
    },
  });

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isUserIdChecking, setIsUserIdChecking] = useState(false);
  const [isUserIdTaken, setIsUserIdTaken] = useState(false);
  const [userIdHelperText, setUserIdHelperText] = useState('');
  const [userIdHelperType, setUserIdHelperType] = useState<'success' | 'warning' | 'error' | null>(
    null,
  );
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedClientAccountData[]>([]);
  const [showAllLinkedAccounts, setShowAllLinkedAccounts] = useState(false);
  const showUserOnboardingDetails = localStorage.getItem('showUserOnboardingDetails') === 'true';
  const isSendAppInviteAllowed = Object.values(accountActivationsState).every(
    (state) => state.isActive,
  );
  const onboardingProgressRaw = clientData?.onboardingProgress;
  const onboardingProgressGroups: OnboardingProgressGroup[] = Array.isArray(onboardingProgressRaw)
    ? onboardingProgressRaw
    : onboardingProgressRaw
      ? [onboardingProgressRaw]
      : [];

  const payoutProgressGroups: PayoutProgressGroup[] = clientData?.payoutProgress ?? [];
  const settlementOnboardingInvites = clientData?.settlementOnboardingInvites ?? [];
  const settlementProgressGroups: SettlementProgressGroup[] = (() => {
    const grouped = new Map<string, SettlementProgressGroup>();

    const isCompletedStatus = (status?: string | null) => {
      const normalizedStatus = (status || '').trim().toLowerCase();
      return (
        normalizedStatus === 'done'
        || normalizedStatus === 'right tick'
        || normalizedStatus === 'completed'
      );
    };

    const toTimestamp = (value?: string | null) => {
      if (!value) return null;
      const ts = new Date(value).getTime();
      return Number.isFinite(ts) ? ts : null;
    };

    const getLatestCompletedTimestamp = (group: SettlementProgressGroup) => {
      const onboardingCompletedTimestamps = (group.onboarding?.steps || [])
        .filter((step) => isCompletedStatus(step.status))
        .map((step) => toTimestamp(step.dateTime))
        .filter((ts): ts is number => ts !== null);

      const payoutCompletedTimestamps = (group.payout?.steps || [])
        .filter((step) => isCompletedStatus(step.status))
        .map((step) => toTimestamp(step.datetime || step.dateTime || null))
        .filter((ts): ts is number => ts !== null);

      const allCompletedTimestamps = [
        ...onboardingCompletedTimestamps,
        ...payoutCompletedTimestamps,
      ];

      if (!allCompletedTimestamps.length) return null;
      return Math.max(...allCompletedTimestamps);
    };

    const getLatestActivityTimestamp = (group: SettlementProgressGroup) => {
      const onboardingTimestamps = (group.onboarding?.steps || [])
        .map((step) => toTimestamp(step.dateTime))
        .filter((ts): ts is number => ts !== null);

      const payoutTimestamps = (group.payout?.steps || [])
        .map((step) => toTimestamp(step.datetime || step.dateTime || null))
        .filter((ts): ts is number => ts !== null);

      const allTimestamps = [...onboardingTimestamps, ...payoutTimestamps];
      if (!allTimestamps.length) return null;
      return Math.max(...allTimestamps);
    };

    const resolveGroupKey = (
      group: {
        settlementId?: number;
        caseId?: number;
        caseReferenceId?: string;
      },
      source: 'onboarding' | 'payout',
      index: number,
    ) => {
      const settlementPart = typeof group.settlementId === 'number' ? `s:${group.settlementId}` : 's:none';
      const casePart =
        typeof group.caseId === 'number'
          ? `c:${group.caseId}`
          : group.caseReferenceId
            ? `cr:${group.caseReferenceId}`
            : 'c:none';

      if (settlementPart !== 's:none') {
        return `${settlementPart}|${casePart}`;
      }

      return `${casePart}|${source}:${index}`;
    };

    onboardingProgressGroups.forEach((group, index) => {
      const key = resolveGroupKey(group, 'onboarding', index);
      const existing = grouped.get(key);

      grouped.set(key, {
        caseId: existing?.caseId ?? group.caseId,
        caseReferenceId: existing?.caseReferenceId ?? group.caseReferenceId,
        settlementId: existing?.settlementId ?? group.settlementId,
        onboarding: group,
        payout: existing?.payout,
      });
    });

    payoutProgressGroups.forEach((group, index) => {
      const key = resolveGroupKey(group, 'payout', index);
      const existing = grouped.get(key);

      grouped.set(key, {
        caseId: existing?.caseId ?? group.caseId,
        caseReferenceId: existing?.caseReferenceId ?? group.caseReferenceId ?? undefined,
        settlementId: existing?.settlementId ?? group.settlementId,
        onboarding: existing?.onboarding,
        payout: group,
      });
    });

    // Resend eligibility is per settlement. An eligible settlement without any progress yet
    // (e.g. its only invite expired) still gets a group so the resend action is reachable.
    settlementOnboardingInvites.forEach((invite, index) => {
      const key = resolveGroupKey(
        {
          settlementId: invite.settlementId,
          caseId: invite.caseId,
          caseReferenceId: invite.caseReferenceId ?? undefined,
        },
        'onboarding',
        index,
      );
      const existing = grouped.get(key);

      if (!existing && !invite.resendOnboardingInvite) return;

      grouped.set(key, {
        ...existing,
        caseId: existing?.caseId ?? invite.caseId,
        caseReferenceId: existing?.caseReferenceId ?? invite.caseReferenceId ?? undefined,
        settlementId: existing?.settlementId ?? invite.settlementId,
        resendOnboardingInvite: invite.resendOnboardingInvite,
      });
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const aCompletedAt = getLatestCompletedTimestamp(a);
      const bCompletedAt = getLatestCompletedTimestamp(b);

      if (aCompletedAt !== null && bCompletedAt !== null && aCompletedAt !== bCompletedAt) {
        return bCompletedAt - aCompletedAt;
      }

      if (aCompletedAt !== null && bCompletedAt === null) return -1;
      if (aCompletedAt === null && bCompletedAt !== null) return 1;

      const aActivityAt = getLatestActivityTimestamp(a);
      const bActivityAt = getLatestActivityTimestamp(b);

      if (aActivityAt !== null && bActivityAt !== null && aActivityAt !== bActivityAt) {
        return bActivityAt - aActivityAt;
      }

      if (aActivityAt !== null && bActivityAt === null) return -1;
      if (aActivityAt === null && bActivityAt !== null) return 1;

      if (typeof a.settlementId === 'number' && typeof b.settlementId === 'number') {
        return b.settlementId - a.settlementId;
      }

      if (typeof a.settlementId === 'number') return -1;
      if (typeof b.settlementId === 'number') return 1;

      return (a.caseReferenceId || '').localeCompare(b.caseReferenceId || '');
    });
  })();

  const getPayoutReferenceText = (
    steps: (typeof payoutProgressGroups)[number]['steps'],
    paymentMethod: string | null,
  ) => {
    const traceNumber = steps.find((step) => !!step.traceNumber)?.traceNumber;
    const checkNumber = steps.find((step) => !!step.checkNumber)?.checkNumber;
    const normalizedMethod = (paymentMethod || '').trim().toUpperCase();

    return normalizedMethod === 'CHECK'
      ? checkNumber
        ? ` | Check Number: ${checkNumber}`
        : ''
      : traceNumber
      ? ` | Trace Number: ${traceNumber}`
      : '';
  };

  const isHaltedStep = (status?: string | null, failureType?: string | null) => {
    const normalizedStatus = (status || '').trim().toLowerCase();
    const hasFailureType = typeof failureType === 'string' && failureType.trim().length > 0;
    return (
      hasFailureType
      || ['failed', 'filed', 'stopped', 'cancelled', 'canceled', 'error'].includes(normalizedStatus)
    );
  };

  const getOnboardingStepPresentation = (
    status: string,
    failureType?: string | null,
    isFlowHalted = false,
  ) => {
    const normalizedStatus = status.trim().toLowerCase();

    if (isHaltedStep(status, failureType)) {
      return {
        color: '#F06A6A',
        icon: <HighlightOffIcon sx={{ color: '#F06A6A', fontSize: '20px' }} />,
      };
    }

    if (
      normalizedStatus === 'done'
      || normalizedStatus === 'right tick'
      || normalizedStatus === 'completed'
    ) {
      return {
        color: '#5ECF95',
        icon: <CheckCircleOutlineIcon sx={{ color: '#5ECF95', fontSize: '20px' }} />,
      };
    }

    if (normalizedStatus === 'in progress') {
      return {
        color: styles.theme.palette.warning.main,
        icon: <HourglassEmptyIcon sx={{ color: styles.theme.palette.warning.main, fontSize: '20px' }} />,
      };
    }

    // Waiting steps show an hourglass unless the flow has been halted by a failed step
    return {
      color: 'rgba(255, 255, 255, 0.45)',
      icon: isFlowHalted ? (
        <RadioButtonUncheckedIcon sx={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '20px' }} />
      ) : (
        <HourglassEmptyIcon sx={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '20px' }} />
      ),
    };
  };

  const getStepDisplayStatus = (status?: string | null, failureType?: string | null) =>
    typeof failureType === 'string' && failureType.trim().length > 0
      ? failureType
          .trim()
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (char) => char.toUpperCase())
      : status || 'Pending';

  const isCompletedStep = (status?: string | null) =>
    ['done', 'right tick', 'completed'].includes((status || '').trim().toLowerCase());

  const getFormattedOnboardingDate = (dateTime?: string | null) => {
    if (!dateTime) {
      return '';
    }

    const parsed = moment(dateTime);
    return parsed.isValid() ? parsed.format('M/D/YYYY, h:mm:ss A') : dateTime;
  };

  const formMethods = useForm<ClientEditFormSubmitProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(clientEditFormSchema) as never,
    defaultValues: CLIENT_EDIT_FORM_DEFAULT_VALUES,
  });

  const personalInfoFormMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(personalInfoFormSchema),
    defaultValues: {
      birthdate: moment(),
      ssn: '***-**-****',
    },
  });

  const {
    handleSubmit,
    reset,
    trigger,
    getValues,
    formState: { isValid, isDirty },
  } = formMethods;

  const {
    setValue,
    watch,
    reset: resetPersonalInfoForm,
    formState: { isDirty: personalFormIsDirty },
  } = personalInfoFormMethods;

  const ssn = watch('ssn');
  const birthdate = watch('birthdate');
  const watchedUserId = formMethods.watch('userId');
  const userIdFieldError = formMethods.formState.errors.userId?.message as string | undefined;

  useEffect(() => {
    // Any edit invalidates the previous availability result
    setIsUserIdTaken(false);
    // Drop any stale availability message while the user is typing
    setUserIdHelperText('');
    setUserIdHelperType(null);
    if (!watchedUserId?.trim()) {
      setUserIdHelperText('Leave blank to keep auto-generated/default user ID behavior.');
      setUserIdHelperType(null);
    }
  }, [watchedUserId]);

  const onSubmitForm = (values: ClientEditFormSubmitProps) => {
    if (canEditUserId && isUserIdTaken) {
      setAlert((prev) => ({
        ...prev,
        message: 'User ID is already taken. Choose a different one.',
        type: 'error',
      }));
      return;
    }
    setIsFormSubmitting(true);

    const selectedOnboardingOption = normalizeOnboardingOptionValue(values.onboardingOption);

    const submitData = {
      address2: values.address2?.length ? values.address2 : null,
      middleName: values.middleName?.length ? values.middleName : null,
      referenceId: values.referenceId,
      ...(canEditUserId && {
        userId: values.userId?.trim().length ? values.userId.trim().toLowerCase() : null,
      }),
      firstName: values.firstName,
      lastName: values.lastName,
      address1: values.address1,
      city: values.city,
      countryState: values.state,
      zip: values.zip,
      onboardingConfiguration: {
        authType: selectedOnboardingOption,
        linkDeliveryChannel: values.onboardingLinkDeliveryChannel || null,
        otpChannel: values.onboardingOtpChannel || null,
      },
      ...(!hasCognitoId
        ? {
          email: values.email,
          phone: values.phone?.length ? values.phone : null,
        }
        : {}),
    };

    updateClientRecord(clientId as string, submitData)
      .then((res) => {
        if (!res.data.error) {
          setIsFormSubmitting(false);
          navigate('/clients');

          setAlert((prev) => ({
            ...prev,
            message: 'Client updated successfully.',
            type: 'success',
          }));
        }
      })
      .catch((err) => {
        setIsFormSubmitting(false);
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleUserIdBlur = async () => {
    if (!canEditUserId) {
      return;
    }

    setIsUserIdChecking(false);

    const isUserIdValid = await trigger('userId');
    if (!isUserIdValid) {
      setUserIdHelperText('');
      setUserIdHelperType(null);
      return;
    }

    const userId = getValues('userId')?.trim().toLowerCase();
    // Reflect the normalized (trimmed, lowercased) value back in the input
    formMethods.setValue('userId', userId ?? '', { shouldDirty: true });
    if (!userId) {
      setUserIdHelperText('Leave blank to keep auto-generated/default user ID behavior.');
      setUserIdHelperType(null);
      return;
    }

    setIsUserIdChecking(true);
    setUserIdHelperType(null);
    setUserIdHelperText('Checking User ID availability...');

    checkClientUserIdAvailability({
      userId,
      customerId: clientData?.customerId,
    })
      .then((res) => {
        if (res.data.available) {
          setIsUserIdTaken(false);
          setUserIdHelperText('User ID is available.');
          setUserIdHelperType('success');
        } else {
          setIsUserIdTaken(true);
          setUserIdHelperText('User ID is already taken. Choose a different one.');
          setUserIdHelperType('error');
        }
      })
      .catch((err) => {
        setUserIdHelperType('error');
        setUserIdHelperText(err?.response?.data?.message || 'Unable to validate User ID right now.');
      })
      .finally(() => {
        setIsUserIdChecking(false);
      });
  };

  const handleCancelChanges = () => {
    setIsBirthDateValueVisible(false);
    setIsSSNValueVisible(false);
    reset();
  };

  const handleResetClientAccount = () => {
    resetClientAccount(clientId as string)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'App invitation has been sent successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const getSSNFieldValue = () => {
    if (!clientId) return;

    if (ssn !== '***-**-****') {
      setIsSSNValueVisible(!isSSNValueVisible);
      return;
    }

    setIsSSNLoading(true);

    getClientSSN(Number(clientId))
      .then((res) => {
        setValue('ssn', res.data.ssn);
        setIsSSNValueVisible(true);
      })
      .catch(() => setIsSSNValueVisible(false))
      .finally(() => setIsSSNLoading(false));
  };

  const getBirhtDateFieldValue = () => {
    if (!clientId) return;

    if (!moment(birthdate).isSame(moment(), 'day')) {
      setIsBirthDateValueVisible(!isBirthDateValueVisible);
      return;
    }

    setIsBirthDateLoading(true);

    getClientBirthDate(Number(clientId))
      .then((res) => {
        setValue('birthdate', moment(res.data.birthdate));
        setIsBirthDateValueVisible(true);
      })
      .catch(() => setIsBirthDateValueVisible(false))
      .finally(() => setIsBirthDateLoading(false));
  };

  const handleSendEmailConfirmation = () => {
    sendEmailConfirmation(Number(clientId))
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Email confirmation has been sent successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleResendInvite = (settlementId: number) => {
    const customerId = clientData?.customerId;

    if (!customerId) {
      setAlert((prev) => ({
        ...prev,
        message: 'Customer id is not available for re-initiating onboarding.',
        type: 'error',
      }));
      return;
    }

    reInitiateCustomerOnboarding(customerId, settlementId)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message:
            'The Onboarding process has been re-initiated. Please check your email for more information.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleLinkedAccountClick = (accountId: number | string) => {
    navigate(`/clients/${accountId}/home`);
  };

  const onEditPersonalButtonClick = () => {
    if (localHasSsnDob) {
      onEditPersonalInfoPopupOpen();
    } else {
      setEditPersonalFormData({ ssn: '', birthdate: null });
    }
    setIsEditPersonalInfoPopupOpen(true);
  };

  const onEditPersonalInfoPopupOpen = () => {
    getClientSSN(Number(clientId)).then((res) =>
      setEditPersonalFormData((prevState) => ({
        ...prevState,
        ssn: res.data.ssn,
      })),
    );

    getClientBirthDate(Number(clientId)).then((res) =>
      setEditPersonalFormData((prevState) => ({
        ...prevState,
        birthdate: moment(res.data.birthdate) as unknown as Date,
      })),
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUpdateNewSSNDoBvalues = (_newValues: { ssn: string; birthdate?: Date | null }) => {
    setLocalHasSsnDob(true);
    resetPersonalInfoForm({ ssn: '***-**-****', birthdate: moment() });
    setIsSSNValueVisible(false);
    setIsBirthDateValueVisible(false);
  };

  useEffect(() => {
    if (entityData) {
      const data: ClientData = entityData as ClientData;
      const onboardingAuthType =
        typeof data.onboardingConfiguration === 'string'
          ? data.onboardingConfiguration
          : data.onboardingConfiguration?.authType;

      reset({
        id: data.id.toString(),
        firstName: data.firstName,
        middleName: data.middleName || '',
        lastName: data.lastName,
        referenceId: data.referenceId,
        userId: data.userId || '',
        email: data.email,
        phone: data.phone || '',
        address1: data.address1,
        address2: data.address2 || '',
        city: data.city,
        state: data.countryState,
        zip: data.zip,
        onboardingOption: normalizeOnboardingOptionValue(onboardingAuthType),
        onboardingConfiguration: data.onboardingConfiguration,
        onboardingLinkDeliveryChannel:
          typeof data.onboardingConfiguration === 'object'
            ? data.onboardingConfiguration?.linkDeliveryChannel || null
            : null,
        onboardingOtpChannel:
          typeof data.onboardingConfiguration === 'object'
            ? data.onboardingConfiguration?.otpChannel || null
            : null,
      });

      if (!data.hasSsnDob) {
        resetPersonalInfoForm({ birthdate: null, ssn: '' });
      }
      setLocalHasSsnDob(data.hasSsnDob);

      setAccountActivationsState({
        email: {
          label: 'EMAIL',
          isActive: data.emailConfirmed,
        },
        ssn: {
          label: 'SSN',
          isActive: data.ssnConfirmed,
        },
        dob: {
          label: 'DATE OF BIRTH',
          isActive: data.dobConfirmed,
        },
      });
    }
  }, [entityData]);

  useEffect(() => {
    if (!clientId) {
      setLinkedAccounts([]);
      setShowAllLinkedAccounts(false);
      return;
    }

    setShowAllLinkedAccounts(false);

    getLinkedClientAccounts(clientId)
      .then((res) => {
        setLinkedAccounts((res.data || []).filter((item) => String(item.id) !== String(clientId)));
      })
      .catch(() => {
        setLinkedAccounts([]);
      });
  }, [clientId]);

  return (
    <>
      {!entityData ? (
        <Loader colorType="warning" />
      ) : (
        <>
          <Box className={styles.classes.pageContainer}>
            <form className={styles.classes.formContainer}>
              <Box className={styles.classes.formInfoContainer}>
                <Box className={styles.classes.formInnerWrapper}>
                  <Box className={styles.classes.formWrapper}>
                    <FormProvider {...formMethods}>
                      <Box display="flex" flexDirection="column" flexBasis="416px">
                        <Typography className={styles.classes.fieldHeadline}>Identity</Typography>
                        <Box mt="16px">
                          <FormCopyTextField entityName="Client" name="id" label="Id" isReadOnly />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="referenceId" label="Reference Id *" disabled={!canEdit} />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="firstName" label="First Name *" disabled={!canEdit} />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="middleName" label="Middle Name" disabled={!canEdit} />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="lastName" label="Last Name *" disabled={!canEdit} />
                        </Box>
                        <Box mt="24px">
                          <FormTextField
                            name="userId"
                            label="User ID"
                            disabled={!canEditUserId}
                            onBlur={() => {
                              void handleUserIdBlur();
                            }}
                            inputProps={{ maxLength: 80 }}
                          />
                          <Box mt="6px" display="flex" alignItems="center" gap="6px">
                            <Typography
                              sx={{
                                fontFamily: 'Inter',
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: userIdFieldError
                                  ? '#F06A6A'
                                  : userIdHelperType === 'success'
                                    ? '#5ECF95'
                                    : userIdHelperType === 'warning'
                                      ? styles.theme.palette.warning.main
                                      : userIdHelperType === 'error'
                                        ? '#F06A6A'
                                        : 'rgba(255,255,255,0.7)',
                              }}
                            >
                              {canEditUserId
                                ? userIdFieldError ? ' ' : userIdHelperText || 'Leave blank to keep auto-generated/default user ID behavior.'
                                : 'User ID belongs to the customer and cannot be changed.'}
                            </Typography>
                            {isUserIdChecking && canEditUserId && (
                              <CircularProgress size={12} sx={{ color: 'info.main' }} />
                            )}
                          </Box>
                        </Box>

                        <Typography className={styles.classes.fieldHeadline} mt="32px">
                          Credentials
                        </Typography>
                        <Box mt="16px">
                          <FormTextField
                            name="email"
                            label="Email Address *"
                            disabled={!canEditEmailAndPhone}
                          />
                        </Box>
                        <Box mt="24px">
                          <FormMaskedTextField
                            mask="+19999999999"
                            name="phone"
                            label="Mobile Number"
                            disabled={!canEditEmailAndPhone}
                          />
                        </Box>

                        <Box mt="32px">
                          <OnboardingConfigurationCard
                            disabled={!canEdit}
                            firmConfigValue={firmConfigValue}
                            widthMode="full"
                          />
                        </Box>
                      </Box>
                    </FormProvider>
                    <Box display="flex" flexDirection="column" flexBasis="416px">
                      <FormProvider {...formMethods}>
                        <Typography className={styles.classes.fieldHeadline}>Address</Typography>
                        <Box mt="16px">
                          <FormTextField name="address1" label="Address Line 1 *" disabled={!canEdit} />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="address2" label="Address Line 2" disabled={!canEdit} />
                        </Box>
                        <Box className={styles.classes.formPlaceInfoContainer}>
                          <FormTextField name="city" label="City *" disabled={!canEdit} />
                          <FormSelectField
                            options={US_STATES_LIST}
                            name="state"
                            label="State *"
                            width="100%"
                            disabled={!canEdit}
                          />
                          <FormTextField name="zip" label="Zip *" disabled={!canEdit} />
                        </Box>
                      </FormProvider>

                      <FormProvider {...personalInfoFormMethods}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                          <Typography className={styles.classes.fieldHeadline} mt="32px">
                            Personal Information
                          </Typography>
                          <IconButton
                            className={styles.classes.editPersonalInfoButton}
                            onClick={onEditPersonalButtonClick}
                            disableFocusRipple
                            disabled={
                              !canEdit ||
                              (accountActivationsState.dob.isActive &&
                              accountActivationsState.ssn.isActive)
                            }
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </Box>
                        <Box mt="16px">
                          {localHasSsnDob ? (
                            <FormDateField
                              name="birthdate"
                              label="Date of Birth"
                              maxDate={moment()}
                              readonly
                              isEncrypted
                              isEncryptedValueLoading={isBirthDateLoading}
                              isValueVisible={isBirthDateValueVisible}
                              onRightIconClick={getBirhtDateFieldValue}
                            />
                          ) : (
                            <Box
                              onClick={canEdit ? onEditPersonalButtonClick : undefined}
                              sx={{ cursor: canEdit ? 'pointer' : 'default', '& *': { pointerEvents: 'none' }, '& .MuiInputAdornment-positionEnd': { display: 'none' } }}
                            >
                              <FormDateField
                                name="birthdate"
                                label="Date of Birth"
                                maxDate={moment()}
                              />
                            </Box>
                          )}
                        </Box>
                        <Box mt="24px">
                          {localHasSsnDob ? (
                            <FormSSNField
                              name="ssn"
                              label="Social Security Number"
                              isEncrypted
                              isEncryptedValueLoading={isSSNLoading}
                              isValueVisible={isSSNValueVisible}
                              onRightIconClick={getSSNFieldValue}
                            />
                          ) : (
                            <Box
                              onClick={canEdit ? onEditPersonalButtonClick : undefined}
                              sx={{ cursor: canEdit ? 'pointer' : 'default', '& *': { pointerEvents: 'none' } }}
                            >
                              <FormTextField
                                name="ssn"
                                label="Social Security Number"
                              />
                            </Box>
                          )}
                        </Box>
                      </FormProvider>

                      <Typography className={styles.classes.fieldHeadline} mt="32px">
                        Confirmations
                      </Typography>

                      <Box mt="12px" width="100%">
                        <ClientActivationsContainer
                          buttonGroupLabel="identity"
                          maxWidth="100px"
                          accountActivationsState={accountActivationsState}
                        />
                      </Box>

                      <Typography className={styles.classes.fieldHeadline} mt="32px">
                        Activations
                      </Typography>
                      <Box mt="12px" width="100%">
                        <ClientActivationsButtonGroup
                          buttonGroupLabel="app"
                          buttonsList={CLIENT_ACTIVATION_BUTTON_GROUPS.app}
                          maxWidth="100px"
                        />
                      </Box>

                      <Box mt="16px">
                        <ClientActivationsButtonGroup
                          buttonGroupLabel="virtual card"
                          buttonsList={CLIENT_ACTIVATION_BUTTON_GROUPS.virtualCard}
                          maxWidth="80px"
                        />
                      </Box>

                      <Box mt="16px">
                        <ClientActivationsButtonGroup
                          buttonGroupLabel="physical card"
                          buttonsList={CLIENT_ACTIVATION_BUTTON_GROUPS.physicalCard}
                          maxWidth="90px"
                        />
                      </Box>
                    </Box>
                  </Box>

                  {canEdit && (
                  <Box display="flex" width="100%" justifyContent="center">
                    <Box
                      display="flex"
                      mt="32px"
                      maxWidth="416px"
                      flexDirection="column"
                      gap="16px"
                      width="100%"
                    >
                      <FormSubmitButton
                        disabled={
                          !isValid || personalFormIsDirty ? false : !isDirty || isFormSubmitting
                        }
                        buttonIcon={<SaveIcon />}
                        buttonText="Save"
                        onClick={handleSubmit(onSubmitForm)}
                      />
                      <FormCancelButton disabled={!isDirty} onClick={handleCancelChanges} />
                    </Box>
                  </Box>
                  )}
                </Box>

                {canEdit && (
                <Box className={styles.classes.formActionsWrapper}>
                  <Typography className={styles.classes.fieldHeadline}>Actions</Typography>

                  <Box mt="16px">
                    <CommonButton
                      type="success"
                      buttonText="send app invite"
                      onButtonClick={handleResetClientAccount}
                      disabled={!isSendAppInviteAllowed}
                    />
                  </Box>

                  <Box mt="16px">
                    <CommonButton
                      type="success"
                      buttonText="Send email confirmation"
                      onButtonClick={handleSendEmailConfirmation}
                      disabled={!!entityData?.emailConfirmed}
                    />
                  </Box>

                  {/* <Box mt="16px">
                      <CommonButton
                        type="info"
                        buttonText="archive"
                        buttonIcon={<ArchiveIcon />}
                        disabled
                      />
                    </Box> */}

                </Box>
                )}
              </Box>
            </form>

            <Box className={styles.classes.linkedClaimantsContainer}>
              <Typography className={styles.classes.linkedClaimantsTitle}>Linked Claimants</Typography>
              <Box className={styles.classes.linkedClaimantsList}>
                {linkedAccounts.length ? (
                  (showAllLinkedAccounts ? linkedAccounts : linkedAccounts.slice(0, 3)).map((account) => {
                    const accountName = `${account.firstName} ${account.middleName || ''} ${account.lastName}`
                      .replace(/\s+/g, ' ')
                      .trim();
                    return (
                      <Box
                        key={account.id}
                        className={styles.classes.linkedClaimantRow}
                        onClick={() => handleLinkedAccountClick(account.id)}
                      >
                        <Box>
                          <Typography className={styles.classes.linkedClaimantName}>
                            {accountName}
                          </Typography>
                          <Typography className={styles.classes.linkedClaimantMeta}>
                            {account.referenceId}
                          </Typography>
                        </Box>

                        <Box className={styles.classes.linkedClaimantActions}>
                          <ChevronRightIcon className={styles.classes.linkedClaimantChevron} />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                    No linked accounts found in this firm.
                  </Typography>
                )}

                {linkedAccounts.length > 3 && (
                  <Typography
                    onClick={() => setShowAllLinkedAccounts((prev) => !prev)}
                    sx={{
                      fontFamily: 'Inter',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'info.main',
                      cursor: 'pointer',
                      width: 'fit-content',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {showAllLinkedAccounts
                      ? 'Show less linked accounts'
                      : `Show +${linkedAccounts.length - 3} more linked accounts`}
                  </Typography>
                )}

                {!!linkedAccounts.length && (
                  <Typography className={styles.classes.linkedClaimantsHint}>
                    These claimants share a contact channel.
                  </Typography>
                )}
              </Box>
            </Box>

            {showUserOnboardingDetails && settlementProgressGroups.length > 0 && (
              <Box className={styles.classes.onboardingTimelineContainer}>
                <Typography className={styles.classes.fieldHeadline}>
                  Settlement Progress Timeline
                </Typography>

                <Box className={styles.classes.payoutAccordionGroup}>
                  {settlementProgressGroups.map((group, groupIndex) => {
                    const onboardingSteps = group.onboarding?.steps ?? [];
                    const onboardingPaymentMethod = group.onboarding?.paymentMethod || null;
                    const payoutSteps = group.payout?.steps ?? [];
                    // Halted only when the latest step that has happened is a failure; a later
                    // completed/in-progress step (e.g. a retry or alternate payout) resumes the flow
                    const progressSteps = [
                      ...onboardingSteps.map((step) => ({
                        label: step.label,
                        status: step.status,
                        failureType: step.failureType,
                      })),
                      ...payoutSteps.map((step) => ({
                        label: step.step || step.label,
                        status: step.status,
                        failureType: null,
                      })),
                    ];
                    let latestReachedIndex = -1;
                    for (let i = progressSteps.length - 1; i >= 0; i -= 1) {
                      const step = progressSteps[i];
                      if (
                        isHaltedStep(step.status, step.failureType)
                        || isCompletedStep(step.status)
                        || (step.status || '').trim().toLowerCase() === 'in progress'
                      ) {
                        latestReachedIndex = i;
                        break;
                      }
                    }
                    const latestReachedStep = progressSteps[latestReachedIndex];
                    const isFlowHalted = latestReachedStep
                      ? isHaltedStep(latestReachedStep.status, latestReachedStep.failureType)
                      : false;
                    // Current step: the halted/in-progress step, otherwise the next step after the
                    // last completed one (or the last step once everything is completed). Nothing
                    // reached and no onboarding for this settlement yet → awaiting onboarding.
                    const currentStep =
                      latestReachedStep
                      && isCompletedStep(latestReachedStep.status)
                      && latestReachedIndex < progressSteps.length - 1
                        ? progressSteps[latestReachedIndex + 1]
                        : latestReachedStep
                          ?? (onboardingSteps.length === 0
                            ? { label: 'Awaiting Onboarding', status: 'Pending', failureType: null }
                            : progressSteps[0]);
                    const currentStepPresentation = currentStep
                      ? getOnboardingStepPresentation(
                          currentStep.status || 'Pending',
                          currentStep.failureType,
                          isFlowHalted,
                        )
                      : null;
                    const payoutPaymentMethod = group.payout?.paymentMethod || null;
                    const referenceText = getPayoutReferenceText(payoutSteps, payoutPaymentMethod);
                    const paymentMethod = onboardingPaymentMethod || payoutPaymentMethod;
                    const caseLabel = group.caseReferenceId;
                    const settlementLabel = group.settlementId ? `Settlement #${group.settlementId}` : null;
                    const hasTitleParts = !!caseLabel || !!settlementLabel;

                    return (
                      <Accordion
                        key={`settlement-group-${group.caseId ?? groupIndex}-${group.settlementId ?? groupIndex}`}
                        defaultExpanded={groupIndex === 0}
                        className={styles.classes.payoutAccordionRoot}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                          className={styles.classes.payoutAccordionSummary}
                        >
                          <Typography component="div" className={styles.classes.payoutAccordionTitle}>
                            {hasTitleParts ? (
                              <>
                                {caseLabel && (
                                  <Box className={styles.classes.payoutAccordionTitleItem}>
                                    <span>{caseLabel}</span>
                                    {!!group.caseId && (
                                      <Box onClick={(event) => event.stopPropagation()}>
                                        <FormLinkButton path={`/cases/${group.caseId}/home`} />
                                      </Box>
                                    )}
                                  </Box>
                                )}
                                {caseLabel && settlementLabel && (
                                  <span className={styles.classes.payoutAccordionTitleSeparator}>—</span>
                                )}
                                {settlementLabel && (
                                  <Box className={styles.classes.payoutAccordionTitleItem}>
                                    <span>{settlementLabel}</span>
                                    {!!group.settlementId && (
                                      <Box onClick={(event) => event.stopPropagation()}>
                                        <FormLinkButton
                                          path={`/payments/settlement/${group.settlementId}`}
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </>
                            ) : (
                              `Payout ${groupIndex + 1}`
                            )}
                          </Typography>

                          <Box ml="auto" pl="12px" display="flex" alignItems="center" gap="12px">
                            {currentStep && currentStepPresentation && (
                              <Tooltip
                                title={`Current step: ${currentStep.label || 'Pending'} - ${getStepDisplayStatus(
                                  currentStep.status,
                                  currentStep.failureType,
                                )}`}
                                arrow
                                placement="top"
                              >
                                <Box className={styles.classes.currentStepBadge}>
                                  {currentStepPresentation.icon}
                                  <span style={{ color: currentStepPresentation.color }}>
                                    {currentStep.label || 'Pending'}
                                  </span>
                                </Box>
                              </Tooltip>
                            )}

                            {canEdit && typeof group.settlementId === 'number' && (
                              <Box
                                display="flex"
                                alignItems="center"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <Chip
                                  label="Resend Invite"
                                  variant="outlined"
                                  disabled={group.resendOnboardingInvite !== true}
                                  onClick={() => handleResendInvite(group.settlementId as number)}
                                  className={styles.classes.resendInviteChip}
                                />
                              </Box>
                            )}
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails className={styles.classes.payoutAccordionDetails}>
                          <Typography className={styles.classes.onboardingTimelineTitle}>
                            Onboarding Progress
                          </Typography>

                          {onboardingSteps.length ? (
                            <Box className={styles.classes.onboardingTimelineList}>
                              {onboardingSteps.map((step, index) => {
                                const { icon, color } = getOnboardingStepPresentation(
                                  step.status || 'Pending',
                                  step.failureType,
                                  isFlowHalted,
                                );
                                const displayStatus = getStepDisplayStatus(step.status, step.failureType);
                                const formattedDate = getFormattedOnboardingDate(step.dateTime);

                                return (
                                  <Box
                                    key={`onboarding-${groupIndex}-${step.label}-${index}`}
                                    className={styles.classes.onboardingTimelineRow}
                                  >
                                    <Box className={styles.classes.onboardingTimelineIconColumn}>
                                      {icon}
                                      {index < onboardingSteps.length - 1 && (
                                        <Box className={styles.classes.onboardingTimelineConnector} />
                                      )}
                                    </Box>

                                    <Box className={styles.classes.onboardingTimelineContent}>
                                      <Typography className={styles.classes.onboardingTimelineTitle}>
                                        {step.label}
                                      </Typography>
                                      <Typography className={styles.classes.onboardingTimelineMeta}>
                                        <span style={{ color }}>{displayStatus}</span>
                                        {formattedDate ? ` - ${formattedDate}` : ''}
                                      </Typography>
                                      {!!step.message && (
                                        <Typography className={styles.classes.onboardingTimelineMessage}>
                                          {step.message}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography className={styles.classes.onboardingTimelineEmptyText}>
                              Onboarding progress is not available for this settlement.
                            </Typography>
                          )}

                          <Typography className={styles.classes.onboardingTimelineTitle} sx={{ marginTop: '16px' }}>
                            Payout Progress
                          </Typography>

                          {payoutSteps.length ? (
                            <Box className={styles.classes.onboardingTimelineList}>
                              {payoutSteps.map((step, index) => {
                                const { icon, color } = getOnboardingStepPresentation(
                                  step.status || 'Pending',
                                  null,
                                  isFlowHalted,
                                );
                                const payoutStepLabel = step.step || step.label;
                                const claimantPortalStatusRaw = (step as Record<string, unknown>)[
                                  'claimantStep'
                                ];
                                const claimantPortalStatus =
                                  typeof claimantPortalStatusRaw === 'string'
                                    ? claimantPortalStatusRaw.trim()
                                    : '';
                                const formattedDate = getFormattedOnboardingDate(
                                  step.datetime || step.dateTime,
                                );
                                const shouldShowErrorMessage = isHaltedStep(step.status);

                                return (
                                  <Box
                                    key={`payout-${groupIndex}-${payoutStepLabel}-${index}`}
                                    className={styles.classes.onboardingTimelineRow}
                                  >
                                    <Box className={styles.classes.onboardingTimelineIconColumn}>
                                      {icon}
                                      {index < payoutSteps.length - 1 && (
                                        <Box className={styles.classes.onboardingTimelineConnector} />
                                      )}
                                    </Box>

                                    <Box className={styles.classes.onboardingTimelineContent}>
                                      <Box className={styles.classes.onboardingTimelineTitleRow}>
                                        <Typography className={styles.classes.onboardingTimelineTitle}>
                                          {payoutStepLabel || 'Pending'}
                                        </Typography>
                                        {claimantPortalStatus && (
                                          <Tooltip
                                            title={`Claimant Portal Status: ${claimantPortalStatus}`}
                                            arrow
                                            placement="top-start"
                                            componentsProps={{
                                              tooltip: {
                                                sx: (theme) => ({
                                                  backgroundColor: theme.palette.common.white,
                                                  color: theme.palette.primary.light,
                                                  borderRadius: '5px',
                                                  boxShadow: '3px',
                                                  fontFamily: 'Inter',
                                                  fontSize: '13px',
                                                  fontWeight: 500,
                                                  padding: '5px',
                                                }),
                                              },
                                              arrow: {
                                                sx: (theme) => ({
                                                  color: theme.palette.common.white,
                                                }),
                                              },
                                            }}
                                          >
                                            <InfoOutlinedIcon
                                              className={styles.classes.onboardingTimelineHintIcon}
                                            />
                                          </Tooltip>
                                        )}
                                      </Box>
                                      <Typography className={styles.classes.onboardingTimelineMeta}>
                                        <span style={{ color }}>{step.status || 'Pending'}</span>
                                        {formattedDate ? ` - ${formattedDate}` : ''}
                                      </Typography>
                                      {!!step.message && (
                                        <Typography className={styles.classes.onboardingTimelineMessage}>
                                          {step.message}
                                        </Typography>
                                      )}
                                      {shouldShowErrorMessage && !!step.errorMessage && (
                                        <Typography
                                          className={styles.classes.onboardingTimelineMessage}
                                          sx={{ color: '#F8A2A2' }}
                                        >
                                          {step.errorMessage}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography className={styles.classes.onboardingTimelineEmptyText}>
                              Payout progress is not available for this settlement.
                            </Typography>
                          )}

                          {!!paymentMethod && (
                            <Typography className={styles.classes.onboardingTimelineInfoText}>
                              Payment Method: {paymentMethod}
                              {referenceText}
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              </Box>
            )}

            <ClientUpdateSSNDobForm
              isOpen={isEditPersonalInfoPopupOpen}
              onSecondaryButtonClick={() => setIsEditPersonalInfoPopupOpen(false)}
              formData={editPersonalFormData}
              clientId={Number(clientId)}
              handleUpdateNewSSNDoBvalues={handleUpdateNewSSNDoBvalues}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default ClientsEditFormContainer;